/**
 * ストーリーボード本編のセグメント分割 (動画カード単位)。
 * FCP7 XML / FCPXML / 動画エディタ連携 (buildEditProjectFromScenario) で共有する。
 */

/** 分割に必要な最小限のシーン情報 (SceneDownloadData はこれを構造的に満たす)。 */
export interface StoryboardSegmentScene {
  sceneOrder: number;
  /** 動画カードの担当範囲 (generation_metadata.scene_range)。動画を持つシーンでのみ意味を持つ */
  sceneRange?: { start: number; end: number };
  /** ブランドカット (start/end) は本編セグメントから除外される */
  brandCutPosition?: 'start' | 'end' | null;
}

/** generation_metadata から動画カードの担当範囲 (scene_range) を取り出す。 */
export function parseSceneRange(
  generationMetadata: unknown,
): { start: number; end: number } | undefined {
  const rawRange = (generationMetadata as
    { scene_range?: { start?: unknown; end?: unknown } } | null | undefined)?.scene_range;
  const start = Number(rawRange?.start);
  const end = Number(rawRange?.end);
  return rawRange && Number.isFinite(start) && Number.isFinite(end) ? { start, end } : undefined;
}

export interface StoryboardSegment {
  /** セグメントの動画を持つシーン (アンカー) の scenes 配列 index */
  anchorIdx: number;
  /** セグメントが覆う本編シーンの index 列 (タイムライン順) */
  sceneIdxs: number[];
}

/**
 * 本編シーンを動画カードのセグメントへ割り当てる。
 *
 * 動画カードの動画はアンカーシーンの video_material_id に保存されるが、実際の
 * 担当範囲は generation_metadata.scene_range (sceneRange) が正で、アンカー位置と
 * 一致しないことがある (例: カード2がシーン2にアンカーされたまま範囲4-9を担当)。
 * アンカー位置で区切るとテロップ/ナレーションのコマ割りが担当範囲とズレるため、
 * 境界は各カードの担当開始 order (保存済み範囲の start、無ければアンカーの order)
 * で決め、各本編シーンは「担当開始が自分の order 以下で最大のカード」に属させる。
 * 先頭カードより前のシーンは、先頭カードの範囲が未保存なら先頭カードへ、保存済みなら
 * 未カバー (下記) にする。範囲未保存のカードのみの場合は従来の
 * 「動画を持つシーンで区切る」分割と完全に一致する。
 *
 * 保存済み範囲を持つカードは end を担当上限として尊重する。担当カードの範囲を
 * 超えていて後続カードにも属さないシーン (例: 「特定シーンだけ生成」で動画1が
 * 範囲1-5のみ生成済み、動画2は未生成のときのシーン6以降) はどのセグメントにも
 * 含めず「未カバー」として返す。先頭カードの保存済み start より前のシーン (例:
 * シーン1を予告編として残し、動画1をシーン1にアンカーしたまま範囲2-4で生成した
 * ときのシーン1) も同様に未カバー (アンカー自身でも動画はセグメント側に置く)。
 * 呼び出し側では動画に紐付けず、コマ尺のままテロップ/ナレーションだけを配置する。
 */
export function computeStoryboardSegments(
  scenes: readonly StoryboardSegmentScene[],
  sceneHasVideo: (idx: number) => boolean,
): StoryboardSegment[] {
  const bodyIndices = scenes
    .map((s, i) => (s.brandCutPosition ? -1 : i))
    .filter((i) => i >= 0);
  const anchors = bodyIndices.filter(sceneHasVideo);
  if (anchors.length === 0) return [];

  const cards = anchors
    .map((anchorIdx) => ({
      anchorIdx,
      startOrder: scenes[anchorIdx].sceneRange?.start ?? scenes[anchorIdx].sceneOrder,
      endOrder: scenes[anchorIdx].sceneRange?.end ?? Number.POSITIVE_INFINITY,
      hasSavedStart: scenes[anchorIdx].sceneRange !== undefined,
    }))
    .sort((a, b) => (
      a.startOrder - b.startOrder
      || scenes[a.anchorIdx].sceneOrder - scenes[b.anchorIdx].sceneOrder
    ));

  const segments: StoryboardSegment[] = cards.map((c) => ({ anchorIdx: c.anchorIdx, sceneIdxs: [] }));
  for (const i of bodyIndices) {
    const order = scenes[i].sceneOrder;
    let k = 0;
    for (let c = 1; c < cards.length; c++) {
      if (cards[c].startOrder <= order) k = c;
      else break;
    }
    // 担当カードの保存済み範囲を超えるシーンは未カバー (どのセグメントにも含めない)
    if (order > cards[k].endOrder) continue;
    // 先頭カードの保存済み start より前のシーンも未カバー (範囲未保存なら従来どおり吸収)
    if (order < cards[k].startOrder && cards[k].hasSavedStart) continue;
    segments[k].sceneIdxs.push(i);
  }
  // 同一開始 order の重複カード等で担当シーンを持たないセグメントは出さない
  return segments.filter((seg) => seg.sceneIdxs.length > 0);
}
